import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Users,
  Loader2,
  BookOpen,
  CalendarDays,
  UserPlus,
  Map,
  Rocket,
  Building2,
  Maximize2,
  TrendingUp,
  Info,
  LayoutDashboard,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { usePublicStats } from "@/hooks/usePublicStats";
import { useAuth } from "@/contexts/AuthContext";
import { GrainOverlay } from "@/components/ui/GrainOverlay";
import { TextReveal } from "@/components/ui/TextReveal";
import { AnimatedCounter } from "@/components/ui/AnimatedCounter";
import { MagneticButton } from "@/components/ui/MagneticButton";
import { ScrollProgress } from "@/components/ui/ScrollProgress";
import { PRINCIPLES } from "@/data/codeOfConduct";
import heroBg from "@/assets/hero-bg.png";
import fbnLogo from "@/assets/fbn-logo.png";

const LandingPage = () => {
  const { memberCount, chapterCount: uniqueChapters, activeProjects, loading } = usePublicStats();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Logged-in members never see the marketing page - bounce to /dashboard.
  useEffect(() => {
    if (!authLoading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [authLoading, user, navigate]);

  // While auth is hydrating, render a minimal spinner so we never flash the
  // marketing layout to a returning member who's about to be redirected.
  if (authLoading || user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <GrainOverlay />
      <ScrollProgress />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 lg:px-12 py-4 bg-white shadow-sm">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img 
              src={fbnLogo} 
              alt="FBN Entrepreneurs" 
              className="h-14 w-auto"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-center gap-6"
          >
            <Link
              to="/about"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary transition-colors"
            >
              <Info className="w-4 h-4" />
              About
            </Link>
            <Link
              to="/team"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary transition-colors"
            >
              <Users className="w-4 h-4" />
              Team
            </Link>
            {user && (
              <Link
                to="/dashboard"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary transition-colors"
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </Link>
            )}
            {user && (
              <Link
                to="/knowledge-hub"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Knowledge Hub
              </Link>
            )}
            {user && (
              <Link
                to="/events"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary transition-colors"
              >
                <CalendarDays className="w-4 h-4" />
                Events
              </Link>
            )}
            {user ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-background bg-foreground rounded-lg hover:bg-foreground/90 transition-colors shadow-soft"
              >
                Go to Platform
                <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-background bg-foreground rounded-lg hover:bg-foreground/90 transition-colors shadow-soft"
                >
                  Join
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </>
            )}
          </motion.div>
        </div>
      </nav>

      <main id="main-content">
      {/* Hero Section - Editorial Split Layout */}
      <section className="relative min-h-screen flex items-center pt-24 pb-16">
        {/* Mesh gradient background */}
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src={heroBg} 
            alt="" 
            className="absolute inset-0 w-full h-full object-cover object-[30%_center] md:object-center"
          />
          <div className="absolute inset-0 bg-black/15" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left: Editorial Typography */}
            <div className="lg:col-span-7 space-y-8">
              {/* Overline */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex items-center gap-3 mt-6 md:mt-0"
              >
                <div className="h-px w-12 bg-white" />
                <span className="text-xs font-medium uppercase tracking-[0.2em] text-white">
                  FBN Entrepreneurs
                </span>
              </motion.div>

              {/* Main headline - Fluid typography */}
              <div className="space-y-2">
                <h1 className="font-serif font-semibold leading-[0.95] tracking-tight text-white"
                    style={{ 
                      fontSize: "clamp(2rem, 5vw, 4rem)"
                    }}>
                  <TextReveal delay={0.5} staggerDelay={0.08}>
                    Entrepreneurship runs in our blood
                  </TextReveal>
                </h1>
              </div>

              {/* Subheadline */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="text-lg sm:text-xl text-white/90 max-w-lg leading-relaxed"
              >
                Entrepreneurs, intrapreneurs, and investors across generations - learning from and supporting one another.
              </motion.p>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 }}
                className="pt-4"
              >
                <Link to={user ? "/dashboard" : "/signup"}>
                  <MagneticButton
                    className="px-8 py-4 bg-foreground text-background rounded-xl font-medium text-lg hover:bg-foreground/90 transition-all"
                  >
                    {user ? "Go to Platform" : "Explore the Directory"}
                    <ArrowRight className="w-5 h-5" />
                  </MagneticButton>
                </Link>
              </motion.div>
            </div>

            {/* Right: Stats Panel */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="bg-card/80 backdrop-blur-sm rounded-2xl border border-border p-8 shadow-soft-lg"
              >
                <div className="space-y-8">
                  {/* Stat 1 */}
                  <div className="flex items-baseline justify-between border-b border-border/50 pb-6">
                    <div>
                      {loading ? (
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      ) : (
                        <AnimatedCounter
                          value={memberCount}
                          className="text-5xl sm:text-6xl font-serif font-semibold text-foreground"
                        />
                      )}
                      <p className="text-sm text-muted-foreground mt-1">Global Members</p>
                    </div>
                    <Users className="w-8 h-8 text-muted-foreground/30" />
                  </div>

                  {/* Stat 2 */}
                  <div className="flex items-baseline justify-between border-b border-border/50 pb-6">
                    <div>
                      {loading ? (
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      ) : (
                        <AnimatedCounter
                          value={uniqueChapters}
                          className="text-5xl sm:text-6xl font-serif font-semibold text-foreground"
                          delay={0.3}
                        />
                      )}
                      <p className="text-sm text-muted-foreground mt-1">Chapters Worldwide</p>
                    </div>
                    <div className="w-8 h-8 rounded-full border-2 border-muted-foreground/20 flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-cyan" />
                    </div>
                  </div>

                  {/* Stat 3 */}
                  <div className="flex items-baseline justify-between">
                    <div>
                      {loading ? (
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      ) : (
                        <AnimatedCounter
                          value={activeProjects}
                          className="text-5xl sm:text-6xl font-serif font-semibold text-foreground"
                          delay={0.5}
                        />
                      )}
                      <p className="text-sm text-muted-foreground mt-1">Active Projects</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                      <span className="text-xs text-muted-foreground">Live</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="text-xs text-muted-foreground uppercase tracking-widest">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-5 h-8 rounded-full border border-border flex justify-center pt-2"
          >
            <div className="w-1 h-2 rounded-full bg-muted-foreground" />
          </motion.div>
        </motion.div>
      </section>

      {/* What's Inside - feature cards */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-14 max-w-3xl"
          >
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary mb-4">
              What's inside
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-foreground leading-tight">
              Everything you need to build, connect, and grow.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: Users,
                title: "Member Directory",
                description:
                  "Find and filter entrepreneurs, intrapreneurs, expanders, and investors from family businesses worldwide.",
              },
              {
                icon: UserPlus,
                title: "Meaningful Connections",
                description:
                  "Connect with a purpose. Every connection starts with a 15-minute intro call.",
              },
              {
                icon: BookOpen,
                title: "Knowledge Hub",
                description:
                  "Access resources, case studies, and learnings shared by the community.",
              },
              {
                icon: CalendarDays,
                title: "Global Events",
                description:
                  "Discover local meetups, summits, workshops, and webinars happening across the network.",
              },
              {
                icon: Map,
                title: "Interactive Map",
                description:
                  "See where members are based and find entrepreneurs in your region.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-card rounded-2xl border border-border/60 p-6 shadow-soft select-none [-webkit-tap-highlight-color:transparent]"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-serif font-semibold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Belongs Here - 4 quadrants */}
      <section className="py-24 lg:py-32 bg-secondary/30">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-14 max-w-3xl"
          >
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary mb-4">
              Who belongs here
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-foreground leading-tight">
              Four ways to be entrepreneurial in a family business.
            </h2>
            <p className="text-lg text-muted-foreground mt-6 leading-relaxed">
              Whether you're building something new, transforming from within, expanding what exists,
              or backing the next generation - there's a place for you here.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5">
            {[
              {
                icon: Rocket,
                title: "Entrepreneur",
                description:
                  "Building a new venture outside the family business. You're starting from scratch and creating something of your own.",
                color: "hsl(14, 78%, 57%)",
              },
              {
                icon: Building2,
                title: "Intrapreneur",
                description:
                  "Driving innovation inside the family business. You're transforming the company from within.",
                color: "hsl(185, 45%, 45%)",
              },
              {
                icon: Maximize2,
                title: "Expander",
                description:
                  "Growing the family business into new markets, products, or territories. You're scaling what already works.",
                color: "hsl(170, 45%, 45%)",
              },
              {
                icon: TrendingUp,
                title: "Investor",
                description:
                  "Backing entrepreneurs and ventures with capital, expertise, or networks. You're fuelling the next generation.",
                color: "hsl(38, 45%, 50%)",
              },
            ].map((q, i) => (
              <motion.div
                key={q.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-card rounded-2xl border border-border/60 p-8 shadow-soft hover:shadow-soft-md transition-all"
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: `${q.color}1a`, color: q.color }}
                >
                  <q.icon className="w-5 h-5" />
                </div>
                <h3
                  className="text-2xl font-serif font-semibold mb-3"
                  style={{ color: q.color }}
                >
                  {q.title}
                </h3>
                <p className="text-base text-muted-foreground leading-relaxed">
                  {q.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-10 text-center"
          >
            <Link
              to="/article/ambidextro-organisations"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              Learn more about ambidextrous organisations and why this matters for family businesses
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* How We Show Up - 10 principle titles */}
      <section className="py-24 lg:py-32 bg-[hsl(var(--champagne))]/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-14 max-w-3xl"
          >
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary mb-4">
              How we show up
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-foreground leading-tight">
              Our community runs on 10 simple principles.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-x-10 gap-y-5">
            {PRINCIPLES.map((p, i) => (
              <motion.div
                key={p.number}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="flex gap-4 items-baseline border-b border-border/40 pb-4"
              >
                <span className="text-2xl font-serif font-semibold text-primary shrink-0 w-8">
                  {p.number}.
                </span>
                <p className="text-lg font-serif font-semibold text-foreground">
                  {p.title}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-12 text-center"
          >
            <Link
              to="/about"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              Read more about our values and code of conduct
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-[0.04] blur-3xl pointer-events-none"
          style={{ background: "hsl(var(--coral))" }}
        />

        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-foreground leading-tight">
              Meet your community
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {loading ? (
                "Loading member data..."
              ) : (
                <>
                  Join {memberCount} entrepreneurs from business families around the world
                  learning from one another and building together across generations.
                </>
              )}
            </p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-3"
            >
              {!user && (
                <Link to="/signup">
                  <MagneticButton className="px-8 py-4 bg-primary text-primary-foreground rounded-xl font-medium text-base hover:bg-primary/90 transition-all">
                    Join the Community
                    <ArrowRight className="w-5 h-5" />
                  </MagneticButton>
                </Link>
              )}
              <Link
                to={user ? "/dashboard" : "/signup"}
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-foreground/20 text-foreground rounded-xl font-medium text-base hover:border-foreground/40 transition-all"
              >
                {user ? "Go to Platform" : "Browse the Directory"}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="py-12 border-t border-border bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div className="flex items-center gap-4">
              <img 
                src={fbnLogo} 
                alt="FBN Entrepreneurs" 
                className="h-12 w-auto"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} FBN Entrepreneur Community
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
